import { UserTypes } from '@/types/authorization'
import type { AdvicePrepPaywallReason } from '../types'

/**
 * Routes that AdvicePrep gates behind an active subscription, mapped to the
 * label the paywall screen shows. Keys come from routes/protected.
 *
 * Two different mechanisms render the same screen (D-16). Platform, Users and
 * User Groups lose their permissions server-side. The AdvicePrep routes do not
 * — the Advisory AI grant is never revoked — so the guard has to key off the
 * paywall reason rather than waiting for a permission to disappear.
 */
export const ADVICEPREP_PAYWALLED_ROUTE_LABELS = {
  platform: 'Platform',
  users: 'Users',
  'user-groups': 'User Groups',
  advisoryai: 'AdvicePrep',
  'advisoryai-call': 'AdvicePrep',
  'advisoryai-calls': 'AdvicePrep',
  'advisoryai-consolidated': 'AdvicePrep',
  'advisoryai-consolidated-detail': 'AdvicePrep',
  'advisoryai-settings': 'AdvicePrep',
} as const satisfies Record<string, string>

export type AdvicePrepPaywalledRouteKey = keyof typeof ADVICEPREP_PAYWALLED_ROUTE_LABELS

/**
 * The subset whose permissions the API withholds. Only these need forcing back
 * into the nav — the AdvicePrep routes keep their permission and so never
 * disappear from it.
 */
export const ADVICEPREP_PERMISSION_WITHHELD_ROUTE_KEYS = new Set(['platform', 'users', 'user-groups'])

export function advicePrepPaywalledAreaLabel(routeKey: string): string | null {
  return (ADVICEPREP_PAYWALLED_ROUTE_LABELS as Record<string, string>)[routeKey] ?? null
}

/**
 * Route keys are strings on `ProtectedRouteDescriptor`, so nothing links these
 * to the real route table at compile time. Renaming a route there would
 * silently drop it from the paywall — a gate that fails open. This asserts the
 * link at startup instead, where it is loud.
 */
export function assertAdvicePrepPaywalledRoutesExist(knownRouteKeys: ReadonlySet<string>): string[] {
  return Object.keys(ADVICEPREP_PAYWALLED_ROUTE_LABELS).filter((key) => !knownRouteKeys.has(key))
}

/**
 * A DI admin finishes an incomplete signup on the client's behalf, so the
 * paywall asking that client to pay must not lock them out of the client.
 */
export function isExemptFromAdvicePrepPaywall(
  userTypeId: UserTypes | undefined,
  reason: AdvicePrepPaywallReason
): boolean {
  return userTypeId === UserTypes.Admin && reason === 'signup_incomplete'
}

const paywallReasons = new Set(['signup_incomplete', 'past_due', 'subscription_ended'])

/**
 * The paywall screen looks its copy up by reason, so an unrecognised value must
 * be dropped rather than passed through — it would render nothing and throw, a
 * white screen on a gated route instead of a paywall.
 */
export function isAdvicePrepPaywallReason(value: unknown): value is AdvicePrepPaywallReason {
  return typeof value === 'string' && paywallReasons.has(value)
}
